"use client";

import type React from "react";
import { useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { FileText, Download, Mail, ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";

// Import product data
import productsData from "../data/products.json";

interface Product {
  id: string;
  name: string;
  category: string;
  subcategory?: string;
  productType?: string;
  description_extended?: string;
  tags?: string[];
  image: string;
  images?: string[];
  description: string;
  datasheet?: string;
  safetySheet?: string;
  sourceUrl?: string;
  availablePackages?: string[];
  active?: boolean;
}

const isDownloadableDocument = (href?: string) =>
  Boolean(href && (href.startsWith("http") || href.startsWith("/pdf/")));

const packageIconMap: Record<string, string> = {
  "Bottle – 1L": "/images/package/bottle-1l.png",
  "Bottle – 1L/4L": "/images/package/bottle-1l-4l.png",
  "Bottle – 1L/5L": "/images/package/bottle-1l-5l.png",
  "Pail – 18L": "/images/package/pail-18l.png",
  "Keg – 60L": "/images/package/keg-60l.png",
  "Drum – 200L": "/images/package/drum-200l.png",
  "IBC – 1000L": "/images/package/ibc-1000l.png",
  "Bulk – 22500L": "/images/package/bulk-22500l.png",
};

const normalizePackageLabel = (label: string) =>
  label
    .replace(/â€“|â€”|âˆ’/g, "–")
    .replace(/[—−]/g, "–")
    .replace(/\s+/g, " ")
    .trim();

const getPackageIcon = (label: string) => packageIconMap[normalizePackageLabel(label)];

const ProductDetailPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const productsSearch = (location.state as { productsSearch?: string } | null)?.productsSearch || "";
  const productsPath = `/products${productsSearch}`;
  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  
  // Image carousel related states
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  
  // Multiple product images
  const [productImages, setProductImages] = useState<string[]>([]);

  useEffect(() => {
    // Find matching product from data
    const fetchProduct = () => {
      setLoading(true);

      if (productId) {
        console.log("URL productId:", productId);

        if (productId) {
          const foundProduct = productsData.find((p) => p.id === productId && p.active !== false);
          if (foundProduct) {
            setProduct(foundProduct);
            console.log("Found product:", foundProduct.name);

            // Use the product's images array if it exists
            let productImages = [];
            if (foundProduct.images && foundProduct.images.length > 0) {
              productImages = foundProduct.images;
            } else {
              // If there's no images array, use a single image
              productImages = [foundProduct.image || "/images/products/product1.png"];
            }
            setProductImages(productImages);
            
            // Find related products
            findRelatedProducts(foundProduct);
          } else {
            console.log("Product not found for ID:", productId);
            // Product not found, navigate back to product list page
            navigate(productsPath);
          }
        }
      }

      setLoading(false);
    };

    // Find related products function
    const findRelatedProducts = (currentProduct: Product) => {
      // Exclude the current product
      const otherProducts = productsData.filter(
        (p) => p.id !== currentProduct.id && p.active !== false
      );

      // Calculate similarity score for each product
      const productsWithScores = otherProducts.map((p) => {
        let score = 0;

        // Same category score
        if (p.category === currentProduct.category) score += 3;

        if (p.subcategory && p.subcategory === currentProduct.subcategory) score += 2;
        if (p.productType && p.productType === currentProduct.productType) score += 2;

        const commonTags = (p.tags || []).filter((tag) =>
          (currentProduct.tags || []).some(
            (currentTag) =>
              currentTag.toLowerCase().includes(tag.toLowerCase()) ||
              tag.toLowerCase().includes(currentTag.toLowerCase())
          )
        );
        score += commonTags.length * 0.25;

        return { product: p, score };
      });

      // Sort by similarity score and select top 3
      const sortedProducts = productsWithScores
        .sort((a, b) => b.score - a.score)
        .slice(0, 3)
        .map((item) => item.product);

      setRelatedProducts(sortedProducts);
    };

    fetchProduct();
  }, [productId, navigate, productsPath]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentImageIndex(index);
  };

  const openImageModal = () => {
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-6"></div>
          <div className="h-64 bg-gray-200 rounded mb-6"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold mb-4">Product Not Found</h2>
        <p className="text-gray-600 mb-6">Sorry, we couldn't find the product you requested.</p>
        <Link
          to="/products"
          className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-md"
        >
          Return to Product List
        </Link>
      </div>
    );
  }

  // Handle related product click
  const handleRelatedProductClick = (relatedProduct: Product) => {
    navigate(`/products/${relatedProduct.id}`, {
      state: {
        productsSearch,
      },
    });
    window.scrollTo(0, 0);
  };

  const renderDocumentLink = (
    href: string | undefined,
    label: "PDS" | "SDS",
    variant: "primary" | "secondary"
  ) => {
    if (!isDownloadableDocument(href)) {
      return (
        <span className="flex items-center bg-gray-100 text-gray-600 px-4 py-2 rounded-md">
          {label === "PDS" ? <FileText size={18} className="mr-2" /> : <Download size={18} className="mr-2" />}
          {label}: Available upon request
        </span>
      );
    }

    const classes =
      variant === "primary"
        ? "bg-red-600 hover:bg-red-700 text-white"
        : "bg-gray-100 hover:bg-gray-200 text-gray-800";

    return (
      <a
        href={href}
        download
        className={`flex items-center ${classes} px-4 py-2 rounded-md transition-colors`}
        title={`Download ${label}`}
      >
        {label === "PDS" ? <FileText size={18} className="mr-2" /> : <Download size={18} className="mr-2" />}
        Download {label}
      </a>
    );
  };

  return (
    <div>
      {/* Hero Section - Can keep the page title from the context */}
      <section className="relative bg-red-600 text-white">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('/images/hero/hero1.png')`,
          }}
        ></div>
        <div className="container mx-auto px-4 py-12 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {product.name}
            </h1>
            <p className="text-xl">
              {product.category} - {product.subcategory || product.productType}
            </p>
          </div>
        </div>
      </section>

      {/* Product Detail Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <button
              onClick={() => navigate(productsPath)}
              className="mb-6 text-red-600 hover:text-red-700 font-medium flex items-center"
            >
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
              Back to Products
            </button>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/2 p-4 pl-0 -ml-5">
                  <div className="flex flex-col">
                    {/* Main image display area */}
                    <div 
                      className="relative h-[450px] bg-white border border-gray-200 rounded-lg mb-4 ml-5"
                    >
                      <img
                        ref={imageRef}
                        src={productImages[currentImageIndex]}
                        alt={product.name}
                        className="h-full w-full object-contain"
                        onClick={openImageModal}
                      />
                      
                      {/* Magnifying glass icon */}
                      <div className="absolute top-2 right-2 bg-white bg-opacity-70 p-1 rounded-full">
                        <ZoomIn size={20} className="text-gray-700" />
                      </div>
                      
                      {/* Left/right switch buttons */}
                      {productImages.length > 1 && (
                        <>
                      <button
                            onClick={(e) => { e.stopPropagation(); prevImage(); }}
                            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-70 p-2 rounded-full text-gray-800 hover:bg-opacity-100 z-10"
                            aria-label="Previous image"
                            type="button"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <button
                            onClick={(e) => { e.stopPropagation(); nextImage(); }}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-70 p-2 rounded-full text-gray-800 hover:bg-opacity-100 z-10"
                            aria-label="Next image"
                            type="button"
                      >
                        <ChevronRight size={20} />
                      </button>
                        </>
                      )}
                    </div>
                    
                    {/* Thumbnail area - Only show if there are multiple images */}
                    {productImages.length > 1 && (
                      <div className="flex justify-center space-x-2 overflow-x-auto py-2">
                      {productImages.map((img, index) => (
                        <button
                          key={index}
                          onClick={() => handleThumbnailClick(index)}
                          className={`flex-shrink-0 w-16 h-16 border-2 ${
                            index === currentImageIndex ? 'border-red-600' : 'border-gray-200'
                          } rounded overflow-hidden`}
                        >
                          <img 
                            src={img} 
                            alt={`Thumbnail ${index + 1}`} 
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                    )}
                  </div>
                </div>
                
                <div className="md:w-1/2 p-8">
                  <h2 className="text-3xl font-bold mb-4">{product.name}</h2>
                  <p className="text-gray-600 mb-6">{product.description}</p>

                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      Available Packages
                    </h3>
                    {product.availablePackages && product.availablePackages.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-5 justify-items-center">
                        {product.availablePackages.map((packageLabel) => {
                          const normalizedPackageLabel = normalizePackageLabel(packageLabel);
                          const packageIcon = getPackageIcon(packageLabel);

                          return (
                            <div
                              key={packageLabel}
                              className="w-32 sm:w-36 md:w-40 max-w-full text-center bg-transparent"
                            >
                              {packageIcon ? (
                                <>
                                  <div className="h-24 sm:h-28 md:h-32 flex items-end justify-center">
                                    <img
                                      src={packageIcon}
                                      alt={normalizedPackageLabel}
                                      className="max-h-full max-w-full object-contain"
                                      onError={(event) => {
                                        event.currentTarget.style.display = "none";
                                      }}
                                    />
                                  </div>
                                  <div className="mt-2 text-sm text-gray-700 font-medium">
                                    {normalizedPackageLabel}
                                  </div>
                                </>
                              ) : (
                                <div className="h-24 sm:h-28 md:h-32"></div>
                              )}
                              {packageIcon ? null : (
                                <div className="mt-2 text-sm text-gray-700 font-medium">
                                  {normalizedPackageLabel}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600">
                        Package sizes available upon request.
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-4">
                    {renderDocumentLink(product.datasheet, "PDS", "primary")}
                    {renderDocumentLink(product.safetySheet, "SDS", "secondary")}
                    <Link
                      to="/contact"
                      className="flex items-center bg-white border border-red-600 text-red-600 hover:bg-red-50 px-4 py-2 rounded-md transition-colors"
                    >
                      <Mail size={18} className="mr-2" />
                      Request a Quote
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Related Products section */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold mb-6">Related Products</h2>
            {relatedProducts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedProducts.map((relProduct) => (
                  <div
                    key={relProduct.id}
                    className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => handleRelatedProductClick(relProduct)}
                  >
                    <div className="h-48 bg-gray-50 flex items-center justify-center p-4">
                      <img
                        src={relProduct.image || "/placeholder.svg"}
                        alt={relProduct.name}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <div className="mb-2">
                        <span className="inline-block bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-md capitalize">
                          {relProduct.category}
                        </span>
                        <span className="inline-block bg-gray-100 text-gray-800 text-xs font-medium px-2 py-1 rounded-md ml-2">
                          {relProduct.subcategory || relProduct.productType}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold mb-2">
                        {relProduct.name}
                      </h3>
                      <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                        {relProduct.description}
                      </p>
                      <div className="flex justify-end items-center">
                        <button className="text-red-600 hover:text-red-700 font-medium text-sm flex items-center">
                          View Product Details
                          <svg
                            className="w-4 h-4 ml-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 5l7 7-7 7"
                            ></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border border-gray-200 rounded-lg">
                <p className="text-gray-500">No related products available</p>
              </div>
            )}
          </div>
        </div>
      </section>
      
      {/* Image preview popup */}
      {showImageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90" onClick={closeImageModal}>
          <div className="relative max-w-5xl w-full h-full flex flex-col items-center justify-center p-4">
            <button 
              className="absolute top-4 right-4 text-white p-2 rounded-full hover:bg-gray-800"
              onClick={closeImageModal}
            >
              <X size={24} />
            </button>
            
            <div className="relative w-full h-4/5 flex items-center justify-center">
              <img
                src={productImages[currentImageIndex]}
                alt={product.name}
                className="max-h-full max-w-full object-contain"
              />
              
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-2 bg-white bg-opacity-25 p-2 rounded-full text-white hover:bg-opacity-50"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-2 bg-white bg-opacity-25 p-2 rounded-full text-white hover:bg-opacity-50"
              >
                <ChevronRight size={24} />
              </button>
            </div>
            
            {/* Thumbnail navigation */}
            <div className="flex justify-center gap-3 mt-4">
              {productImages.map((img, index) => (
                <button
                  key={index}
                  onClick={(e) => { e.stopPropagation(); handleThumbnailClick(index); }}
                  className={`w-16 h-16 border-2 rounded overflow-hidden ${
                    index === currentImageIndex ? 'border-red-600' : 'border-gray-500'
                  }`}
                >
                  <img 
                    src={img} 
                    alt={`Thumbnail ${index + 1}`} 
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
